import { Ref, forwardRef, useEffect, useRef, useState } from "react";

import { useMergeRefs } from "../../hooks";

import {
  ToasterProps as BaseToasterProps,
  Toaster as BaseToaster,
  ToastEventType,
} from "./internal/Toaster";

const SHADOW_HOST_DATA_ATTRS = "[data-mfe-portal-name], [data-mfe-name]";

/**
 * Props for the Toaster component
 * @property {number | false} [duration] - Timeout, in ms, before toasts auto-closes. If set to false, toasts will not auto-close
 * @property {string} id - Unique toaster id
 * @property {(toastEvent: ToastEventType) => void} [onToastEvent] - Callback function that is called when toasts are added, updated, or dismissed
 * @property {string} [focusKey] - How a screen-reader will announce the hotkey used to set focus on the first toast
 * @property {number} [toastsBeforeStack] - How many toasts to display before they start stacking
 * @extends Omit<BaseToasterProps, "disableEvents">
 */
export type ToasterProps = Omit<BaseToasterProps, "disableEvents">;

/**
 * Toaster component for managing and displaying toast notifications with anvil2-specific features.
 *
 * Features:
 * - Manages multiple toast notifications in a stack
 * - Handles toast positioning and stacking behavior
 * - Supports custom events for toast management
 * - Automatic toast dismissal with configurable duration
 * - Keyboard navigation and accessibility support
 * - Dialog-aware positioning for modal contexts
 * - Session storage for dialog toaster state
 * - Custom event system for toast lifecycle management
 * - Responsive design with mobile support
 * - Automatic tracking ID generation for analytics
 * - Monolith toaster integration and coordination
 * - Shadow DOM support for micro-frontend environments
 * - A1 toast group compatibility and positioning
 *
 * @example
 * <Toaster
 *   id="main-toaster"
 *   duration={5000}
 *   toastsBeforeStack={3}
 *   onToastEvent={(event) => console.log('Toast event:', event)}
 * />
 *
 * @example
 * <Toaster
 *   id="dialog-toaster"
 *   disableEvents={false}
 *   focusKey="t"
 * />
 */
export const Toaster = forwardRef(
  ({ onToastEvent, ...props }: BaseToasterProps, ref: Ref<HTMLDivElement>) => {
    const { id } = props;

    const toasterRef = useRef<HTMLDivElement>(null);
    const mergedToasterRef = useMergeRefs([toasterRef, ref]);

    const [shouldDisableEvents, setShouldDisableEvents] =
      useState<boolean>(false);

    useEffect(() => {
      const toasters = document.querySelectorAll("[data-anv='toaster']");
      const shadowToasters =
        (
          toasterRef.current?.getRootNode() as Document | ShadowRoot
        )?.querySelectorAll("[data-anv='toaster']") || [];

      // Toaster(s) exist in the document, bail out if this toaster is not the first one
      if (
        toasters.length &&
        id !== toasters[0].getAttribute("data-toaster-id")
      ) {
        setShouldDisableEvents(true);
      } else if (
        // Toaster(s) exist in the shadow dom, bail out if this toaster is not the first one
        shadowToasters.length &&
        id !== shadowToasters[0].getAttribute("data-toaster-id")
      ) {
        setShouldDisableEvents(true);
      }
    }, [id]);

    const a1ToastGroupObserver = useRef<MutationObserver | null>(null);
    const a1ToastGroupObserverConnected = useRef<boolean>(false);
    const [a1ToastGroups, setA1ToastGroups] = useState<Element[]>([]);
    const [toastCount, setToastCount] = useState<number>(0);

    const handleToastEvent = (event: ToastEventType) => {
      const { toastCount: newToastCount } = event;
      setToastCount(newToastCount);
      onToastEvent?.(event);
    };

    const updateA1ToastGroups = () => {
      const mfeRoots = document.querySelectorAll(SHADOW_HOST_DATA_ATTRS);
      const a1ToastGroupsLocal = [
        ...document.getElementsByClassName("ToastGroup"),
      ];

      if (mfeRoots.length > 0) {
        mfeRoots.forEach((el) => {
          setTimeout(() => {
            if (el.shadowRoot) {
              a1ToastGroupsLocal.push(
                ...el.shadowRoot.querySelectorAll(".ToastGroup"),
              );
            }
            setA1ToastGroups(a1ToastGroupsLocal);
          });
        });
      } else {
        setA1ToastGroups(a1ToastGroupsLocal);
      }
    };

    useEffect(() => {
      // set mutation observer for a1 toasts after initial render
      a1ToastGroupObserver.current = new MutationObserver(updateA1ToastGroups);
    }, []);

    /*
      Listen for any new A1 Toast Groups to adjust styles and
      prevent overlapping.
     */
    useEffect(() => {
      if (a1ToastGroupObserver.current !== null) {
        if (toastCount > 0 && a1ToastGroupObserverConnected.current === false) {
          // listen for any new a1 toasts
          a1ToastGroupObserver.current.observe(document.body, {
            childList: true,
            subtree: true,
          });
          a1ToastGroupObserverConnected.current = true;

          // account for already existing a1 toasts
          updateA1ToastGroups();
        } else if (
          toastCount === 0 &&
          a1ToastGroupObserverConnected.current === true
        ) {
          a1ToastGroupObserver.current.disconnect();
          a1ToastGroupObserverConnected.current = false;
        }
      }
    }, [toastCount]);

    useEffect(() => {
      if (a1ToastGroups && a1ToastGroups.length > 0) {
        const toasterHeight =
          toasterRef.current?.firstElementChild?.getBoundingClientRect()
            .height as number;
        a1ToastGroups.forEach((group) => {
          (group as HTMLDivElement).style.top =
            toastCount === 0 ? "16px" : `calc(${toasterHeight}px + 2.5rem)`;
        });
      }
      // eslint-disable-next-line
    }, [a1ToastGroups.length, toastCount]);

    return (
      <BaseToaster
        {...props}
        onToastEvent={handleToastEvent}
        disableEvents={shouldDisableEvents}
        ref={mergedToasterRef}
      />
    );
  },
);

Toaster.displayName = "Toaster";
